<template>
  <transition name="password-fade">
    <div v-if="showPasswordGate" class="password-gate">
      <div class="password-backdrop"></div>

      <div class="password-container">
        <div class="password-logo-wrapper">
          <img
            src="@/assets/logo/logo.svg"
            alt="Tilly Logo"
            class="password-logo"
          />
        </div>

        <div class="password-tagline">
          <span class="tagline-eyebrow">For investors only</span>
          <span class="tagline-line">A peek behind the curtain.</span>
        </div>

        <div class="password-input-wrapper">
          <input
            ref="passwordInput"
            v-model="password"
            type="password"
            class="password-input"
            placeholder="Enter password"
            @keyup.enter="handleSubmit"
            @input="handleInput"
            @focus="isFocused = true"
            @blur="isFocused = false"
          />
          <div class="password-underline" :class="{ active: isFocused }"></div>
        </div>

        <transition name="error-fade">
          <div v-if="error" class="password-error">
            {{ error }}
          </div>
        </transition>

        <button
          class="password-submit"
          :class="{ disabled: !password || isLoading }"
          @click="handleSubmit"
        >
          <span v-if="!isLoading">Continue</span>
          <span v-else class="loading-spinner"></span>
        </button>

        <div class="password-help">
          Email tavishi@travelwithtern.com for access.
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import * as amplitude from '@amplitude/analytics-browser';

export default {
  name: 'InvestorPasswordGate',
  emits: ['authenticated'],
  props: {
    // Server-side auth scope for the investor surface being unlocked.
    authScope: {
      type: String,
      required: true,
    },
    // Distinct localStorage key so unlocking one route doesn't unlock another.
    storageKey: {
      type: String,
      default: 'investor_authenticated',
    },
    // Source label used in Amplitude analytics events.
    analyticsSource: {
      type: String,
      default: 'investor_password_gate',
    },
  },
  data() {
    return {
      password: '',
      error: '',
      isFocused: false,
      isLoading: false,
      showPasswordGate: false,
    };
  },
  computed: {
    expiryKey() {
      return `${this.storageKey}_expires`;
    },
  },
  async mounted() {
    // Skip the gate on localhost so dev doesn't need the prod password.
    if (this.isLocalhost()) {
      this.showPasswordGate = false;
      this.$emit('authenticated');
      return;
    }

    // localStorage (not sessionStorage) so the 24h window survives tab close.
    const token = localStorage.getItem(this.storageKey);
    const expiresAt = parseInt(localStorage.getItem(this.expiryKey) || '0', 10);
    const stillFresh = token && token !== 'true' && Date.now() < expiresAt;

    if (stillFresh && (await this.verifyStoredToken(token))) {
      this.showPasswordGate = false;
      this.$emit('authenticated');
      return;
    }

    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.expiryKey);
    this.showPasswordGate = true;

    this.$nextTick(() => {
      setTimeout(() => {
        if (this.$refs.passwordInput) {
          this.$refs.passwordInput.focus();
        }
      }, 500);
    });
  },
  methods: {
    isLocalhost() {
      const host = window.location.hostname;
      return (
        host === 'localhost' ||
        host === '127.0.0.1' ||
        host === '0.0.0.0' ||
        host === '[::1]' ||
        host.endsWith('.local')
      );
    },
    getTernApiProject() {
      if (process.env.VUE_APP_ENDPOINTS === 'production') {
        return 'tern-1d723';
      }
      if (process.env.VUE_APP_ENDPOINTS === 'staging') {
        return 'tern-staging';
      }
      return 'tern-app-testing';
    },
    getAuthUrl() {
      return `https://us-central1-${this.getTernApiProject()}.cloudfunctions.net/ternAPI/v1/investor-auth`;
    },
    async requestAuth(payload) {
      const response = await fetch(this.getAuthUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: this.authScope,
          ...payload,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(
          data.error || 'Investor authentication failed.',
        );
        error.status = response.status;
        throw error;
      }

      return data;
    },
    async verifyStoredToken(token) {
      try {
        const data = await this.requestAuth({ token });
        return Boolean(data.authenticated);
      } catch {
        return false;
      }
    },
    handleInput() {
      if (this.error) {
        this.error = '';
      }
    },
    async handleSubmit() {
      if (!this.password || this.isLoading) return;

      this.isLoading = true;
      this.error = '';
      amplitude.track('Investor Password Submitted', {
        source: this.analyticsSource,
      });

      try {
        const data = await this.requestAuth({ password: this.password });
        if (!data.authenticated || !data.token || !data.expiresAt) {
          throw new Error('Investor authentication failed.');
        }

        localStorage.setItem(this.storageKey, data.token);
        localStorage.setItem(this.expiryKey, data.expiresAt.toString());
        amplitude.track('Investor Authenticated', {
          source: this.analyticsSource,
        });
        this.$emit('authenticated');
      } catch (error) {
        this.error =
          error.status === 503
            ? 'Investor access is temporarily unavailable. Please email us for access.'
            : 'Incorrect password. Please try again.';
        this.password = '';
        this.isLoading = false;

        this.$refs.passwordInput?.classList.add('shake');
        setTimeout(() => {
          this.$refs.passwordInput?.classList.remove('shake');
        }, 500);
      }
    },
  },
};
</script>

<style scoped>
.password-gate {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
}

.password-backdrop {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      circle at 20% 30%,
      rgba(253, 173, 0, 0.18) 0%,
      transparent 55%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(244, 132, 108, 0.18) 0%,
      transparent 55%
    ),
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(248, 250, 252, 0.92) 100%
    );
  opacity: 0.9;
}

.password-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.75rem;
  padding: 3rem 2.5rem;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(40px) saturate(200%);
  -webkit-backdrop-filter: blur(40px) saturate(200%);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  min-width: 340px;
  max-width: 92vw;
  animation: password-appear 0.6s cubic-bezier(0.25, 0.1, 0.25, 1) both;
}

@keyframes password-appear {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.password-logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-logo {
  width: 72px;
  height: auto;
  opacity: 0;
  animation: logo-fade-in 0.8s ease-out 0.2s both;
  filter: drop-shadow(0 2px 8px rgba(244, 132, 108, 0.25));
}

@keyframes logo-fade-in {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.password-tagline {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  text-align: center;
}

.tagline-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: #94a3b8;
}

.tagline-line {
  font-size: 1.3rem;
  font-weight: 700;
  color: #3c0f09;
  letter-spacing: -0.015em;
}

.password-input-wrapper {
  position: relative;
  width: 100%;
  max-width: 280px;
}

.password-input {
  width: 100%;
  padding: 1rem 1.25rem;
  font-size: 1.125rem;
  font-weight: 400;
  letter-spacing: 0.01em;
  color: #1a1a1a;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: none;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  backdrop-filter: blur(10px);
}

.password-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
  font-weight: 300;
}

.password-input:focus {
  background: rgba(255, 255, 255, 0.97);
  border-color: rgba(244, 132, 108, 0.4);
  box-shadow: 0 4px 16px rgba(244, 132, 108, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.password-input.shake {
  animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

@keyframes shake {
  10%,
  90% {
    transform: translateX(-4px);
  }
  20%,
  80% {
    transform: translateX(4px);
  }
  30%,
  50%,
  70% {
    transform: translateX(-4px);
  }
  40%,
  60% {
    transform: translateX(4px);
  }
}

.password-underline {
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #fdad00 0%, #f4846c 50%, #58a8e1 100%);
  border-radius: 2px;
  transition: width 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  box-shadow: 0 0 8px rgba(244, 132, 108, 0.4);
}

.password-underline.active {
  width: 100%;
}

.password-error {
  color: #ef4444;
  font-size: 0.875rem;
  text-align: center;
  margin-top: -1rem;
  padding: 0.5rem 1rem;
  background: rgba(239, 68, 68, 0.08);
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.password-submit {
  width: 100%;
  max-width: 280px;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: white;
  background: linear-gradient(135deg, #fdad00 0%, #f4846c 50%, #58a8e1 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  box-shadow: 0 4px 16px rgba(244, 132, 108, 0.3),
    0 2px 8px rgba(244, 132, 108, 0.18);
  position: relative;
  overflow: hidden;
}

.password-submit::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.2) 0%,
    transparent 100%
  );
  opacity: 0;
  transition: opacity 0.3s;
}

.password-submit:hover:not(.disabled)::before {
  opacity: 1;
}

.password-submit:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(244, 132, 108, 0.4),
    0 4px 12px rgba(244, 132, 108, 0.3);
}

.password-submit:active:not(.disabled) {
  transform: translateY(0);
}

.password-submit.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.password-help {
  font-size: 0.8rem;
  color: #94a3b8;
  text-align: center;
  margin-top: -0.25rem;
}

.password-fade-enter-active,
.password-fade-leave-active {
  transition: opacity 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.password-fade-enter-from,
.password-fade-leave-to {
  opacity: 0;
}

.error-fade-enter-active,
.error-fade-leave-active {
  transition: all 0.3s ease;
}

.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 600px) {
  .password-container {
    padding: 2rem 1.5rem;
    gap: 1.5rem;
    min-width: 300px;
  }

  .password-logo {
    width: 60px;
  }
  .tagline-line {
    font-size: 1.15rem;
  }
  .password-input {
    font-size: 1rem;
    padding: 0.875rem 1rem;
  }
  .password-submit {
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
  }
}
</style>
